import { z } from 'zod';
import type {
  BaseParameterDetailAddition,
  BaseSessionDetailAddition,
  BaseSqlDetailAddition,
} from '../types';
import { nullishAsUndefined, nullishToEmpty } from './utils';

export const sessionDetailSchema = z.object({
  sessionId: z.number({ required_error: 'Session ID', invalid_type_error: 'Session ID' }),
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
  collectTime: nullishAsUndefined(z.string().optional()),
  isOpenedFromPA: nullishAsUndefined(z.boolean().optional()),
  databaseName: nullishAsUndefined(z.string().optional()),
  init: nullishAsUndefined(z.boolean().optional()),
});

export const sqlDetailSchema = z.object({
  sqlId: z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID'),
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
  databaseName: nullishAsUndefined(z.string().optional()),
  objectName: nullishAsUndefined(z.string().optional()),
  sqlHash: nullishAsUndefined(z.string().optional()),
  sqlHandle: nullishAsUndefined(z.string().optional()),
  statementStartOffset: nullishAsUndefined(z.number().optional()),
  statementEndOffset: nullishAsUndefined(z.number().optional()),
  planHash: nullishAsUndefined(z.string().optional()),
  planHandle: nullishAsUndefined(z.string().optional()),
  sqlTextHashId: nullishAsUndefined(z.string().optional()),
  sessionId: z.preprocess(
    (val) => (val === '' ? null : val),
    nullishAsUndefined(z.coerce.number().optional()),
  ),
});

export const parameterDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    name: nullishAsUndefined(z.string().optional()),
    parameterName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ name, parameterName, ...rest }) => ({
    ...rest,
    parameterName: name ?? parameterName,
  }))
  .pipe(
    z.object({
      parameterName: z.string({ required_error: 'Parameter Name' }).min(1, 'Parameter Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export const documentTextSchema = z.object({
  sqlTextHashId: z.preprocess(
    nullishToEmpty(''),
    z.string({ required_error: 'SQL Text Hash ID' }).min(1, 'SQL Text Hash ID'),
  ),
  sqlId: z.preprocess(nullishToEmpty(''), z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID')),
  instanceId: z.preprocess(
    nullishToEmpty(''),
    z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  ),
});

export type SessionDetailSchema = z.infer<typeof sessionDetailSchema>;
export type SqlDetailSchema = z.infer<typeof sqlDetailSchema>;
export type ParameterDetailSchema = z.infer<typeof parameterDetailSchema>;
export type DocumentTextSchema = z.infer<typeof documentTextSchema>;

export type SqlServerSessionDetailAddition = BaseSessionDetailAddition & SessionDetailSchema;
export type SqlServerSqlDetailAddition = BaseSqlDetailAddition & SqlDetailSchema;
export type SqlServerParameterDetailAddition = Omit<
  BaseParameterDetailAddition & ParameterDetailSchema,
  'parameterName'
>;
